import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  getAvatarColor,
} from '@heidi/design-system/avatar';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

export interface WorkspaceSwitchOverlayProps {
  workspaceId: string;
  workspaceName: string;
  /** The workspace's uploaded icon; the avatar falls back to the initial without one. */
  iconUrl: string | null;
  /** Plays the exit fade; the host unmounts the overlay once it has run. */
  isLeaving?: boolean;
}

// Trims before taking the character: a leading space is truthy, so
// `' Clinic'.charAt(0)` would pass the fallback guard and render a blank
// avatar. Iterates by code point rather than `charAt` so an emoji-led name
// yields the emoji instead of half a surrogate pair.
const initial = (name: string): string => [...name.trim()][0]?.toUpperCase() ?? '?';

/**
 * The full-screen "Loading your workspace" surface shown while the app
 * switches workspace: the target workspace's avatar and name, so the context
 * change reads as deliberate rather than silent.
 *
 * Presentation only. It knows nothing about why or for how long it is shown:
 * the host that mounts it owns the switch state and the timing. Portalled to
 * `document.body` so it covers every layer of the app shell, including the
 * desktop title bar, and never inherits a transformed ancestor.
 */
export const WorkspaceSwitchOverlay = ({
  workspaceId,
  workspaceName,
  iconUrl,
  isLeaving = false,
}: WorkspaceSwitchOverlayProps) => {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      data-testid="workspace-switch-overlay"
      className={cn(
        'fixed inset-0 z-999 flex flex-col items-center justify-center gap-4 bg-surface-page p-6 text-center',
        'duration-300 ease-out motion-reduce:animate-none',
        isLeaving ? 'animate-out fade-out-0 fill-mode-forwards' : 'animate-in fade-in-0'
      )}
    >
      <span className="relative inline-flex shrink-0">
        {/* The ring breathes so the screen reads as working. It is a sibling,
            not a child: the DS Avatar root is overflow-hidden and its ::after is
            taken. -inset-2 is the 4px gap plus the 4px ring a ring-offset would
            give; the gap shows the overlay's own surface. Transform-only per
            the animation guide, and still under reduced motion. */}
        <span
          aria-hidden
          data-testid="workspace-switch-overlay-ring"
          className="pointer-events-none absolute -inset-2 animate-gentle-pulse rounded-full border-4 border-border will-change-transform motion-reduce:animate-none"
        />
        <Avatar color={getAvatarColor(workspaceId)} className="size-24">
          {iconUrl && <AvatarImage src={iconUrl} alt="" />}
          {/* Raw type scale, deliberately: every Typography component renders a
              heading or paragraph element, and the initial is decorative — the
              name is the <h2> below. The DS Avatar also stops at `lg` (40px), so
              no fallback step exists for this 96px circle. */}
          <AvatarFallback className="text-4xl">{initial(workspaceName)}</AvatarFallback>
        </Avatar>
      </span>
      <div className="flex flex-col items-center gap-1">
        <TypographyH2 className="text-text-primary">{workspaceName}</TypographyH2>
        <TypographyP2 className="text-text-secondary">
          <FormattedMessage defaultMessage="Loading your workspace" id="Z2QdpVlRFW" />
        </TypographyP2>
      </div>
    </div>,
    document.body
  );
};
